import { connect, useDispatch } from 'react-redux';
import type { Dispatch } from 'redux';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Form, FormRenderProps } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import { useUnmount } from 'react-use';
import cn from 'classnames';
import { FieldArray, FieldArrayRenderProps } from 'react-final-form-arrays';
import { FormApi } from 'final-form';

import { Row } from 'UIPackage/FormModule/components/Row';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { getUserRoleIsAdmin } from 'AuthPackage/MainModule/selectors/authSelectors';
import { TextInput } from 'UIPackage/MainModule/components/TextInput';
import { IAdminSetting } from 'APIPackage/AdminModule/interfaces/IAdminSetting';
import { INotificationRecord } from 'APIPackage/UserNotificationModule/interfaces/NotificationRecord';
import { INotificationReadStatusUpdateRequest } from 'APIPackage/UserNotificationModule/interfaces/INotificationReadStatusUpdateRequest';
import { AdminSettingCodesEnum } from 'APIPackage/AdminModule/enums/AdminSettingCodesEnum';
import { adminUpdateSettings } from 'PersonalAccountPackage/AdminModule/actions/adminAction';

import { RootActions } from '../../../CorePackage/MainModule/types/RootActions';
import { SelectInputField } from '../../../UIPackage/MainModule/components/SelectInputField';
import { Button } from '../../../UIPackage/MainModule/components/Button';
import { TextInputField } from '../../../UIPackage/MainModule/components/TextInputField';
import { IRootState } from '../../../CorePackage/MainModule/interfaces/IRootState';
import {
  getReadTypeSelectOption,
  getUserNotificationMessages,
  getUserNotificationMessagesTotalCount,
  getUserNotificationPage,
  getUserNotificationSearchText,
  getUserNotificationSettings,
  getUserNotificationUnreadCount,
} from '../selectors/userNotificationSelectors';
import { UserNotificationRow } from './UserNotificationRow';
import styles from './UserNotification.module.scss';
import { notificationReadStatusOptions, UNREAD_SELECT_OPTION } from '../constants/NotificationReadStatusOptions';
import { NotificationSearchTypeEnum } from '../enums/NotificationSearchTypeEnum';
import { ISelectOption } from '../../../UIPackage/MainModule/components/SelectInput';
import {
  userNotificationGetData,
  userNotificationGetFetchSettingByCode,
  userNotificationReadStatusUpdate,
  userNotificationResetInitialFormValues,
  userNotificationSetPage,
  userNotificationSetSearchText,
  userNotificationSetSelectOption,
} from '../actions/UserNotificationAction';
import { COUNT_ON_PAGE } from '../constants';

interface IUserNotificationForm {
  userNotificationMessages: INotificationRecord[];
  searchType: ISelectOption;
  searchText?: string;
}

interface IUserNotificationProps {
  userNotificationMessages: INotificationRecord[];
  selectedNotificationReadOption: ISelectOption;
  userNotificationUnreadCount: number;
  totalCount: number;
  isAdmin: boolean;
  settings: IAdminSetting | undefined;
  searchText: string;
  page: number;
}

const UserNotificationWithoutStore = ({
  userNotificationMessages,
  selectedNotificationReadOption,
  userNotificationUnreadCount,
  isAdmin,
  settings,
  totalCount,
  searchText,
  page,
}: IUserNotificationProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();

  const defaultNotificationReadOption = UNREAD_SELECT_OPTION;

  const [settingValue, setSettingValue] = useState(settings?.value || '');
  const handleLoadMoreButtonClick = useCallback(() => {
    dispatch(userNotificationSetPage(page + 1));
    setTimeout(position => window.scrollTo({ top: position }), 1000, window.scrollY);
  }, [page]);
  const formRef = useRef<FormApi<IUserNotificationForm, Partial<IUserNotificationForm>>>();

  const lastVisibleItemsNumber = (page + 1) * parseInt(COUNT_ON_PAGE, 10);
  const isLoadMoreButtonVisible = totalCount > lastVisibleItemsNumber;

  const loadNotifications = useCallback(
    (pageInit?: string) => {
      const pageCurrent = pageInit || page.toString();

      dispatch(
        userNotificationGetData(
          selectedNotificationReadOption.id as NotificationSearchTypeEnum,
          undefined,
          pageCurrent,
        ),
      );
    },
    [selectedNotificationReadOption, page],
  );

  useEffect(() => {
    loadNotifications();
    dispatch(userNotificationGetFetchSettingByCode(AdminSettingCodesEnum.REMINDER_DAYS_BEFORE_DEADLINE_PLAN));
  }, []);

  useEffect(() => {
    formRef.current?.change('userNotificationMessages', userNotificationMessages);
  }, [userNotificationMessages]);

  useEffect(() => loadNotifications(), [page]);

  useEffect(() => {
    setSettingValue(settings?.value || '');
  }, [settings]);

  const handleChangeSearchType = useCallback(
    (value: ISelectOption) => {
      dispatch(userNotificationSetSelectOption(value));
      dispatch(userNotificationGetData(value.id as NotificationSearchTypeEnum, searchText, '0'));
      dispatch(userNotificationSetPage(0));
    },
    [dispatch, searchText],
  );

  const initialValues: IUserNotificationForm = useMemo(
    () => ({
      userNotificationMessages,
      searchType: selectedNotificationReadOption || defaultNotificationReadOption,
      searchText,
    }),
    [userNotificationMessages],
  );

  const handleFormSubmit = useCallback(
    (formData: IUserNotificationForm) => {
      dispatch(userNotificationGetData(formData.searchType.id as NotificationSearchTypeEnum, formData.searchText));
      dispatch(userNotificationSetPage(0));
    },
    [dispatch],
  );

  useUnmount(() => {
    dispatch(userNotificationResetInitialFormValues());
  });

  function renderForm({ form, handleSubmit, values }: FormRenderProps<IUserNotificationForm>) {
    const { update } = form.mutators;

    if (!formRef.current) {
      formRef.current = form;
    }

    const handleOnNotificationMessageItemClick = useCallback(
      (index: number, data: INotificationRecord, setIsUnreadToFalse?: boolean) => {
        const { isUnread } = data;

        const request: INotificationReadStatusUpdateRequest = {
          isUnread: !data.isUnread,
          userId: data.userNotificationId,
        };

        dispatch(userNotificationReadStatusUpdate(request));
        update('userNotificationMessages', index, {
          ...data,
          isUnread: setIsUnreadToFalse ? false : !isUnread,
        });
      },
      [],
    );

    const handleSetReadAll = () => {
      const request: INotificationReadStatusUpdateRequest = {
        setReadAll: true,
      };

      dispatch(userNotificationReadStatusUpdate(request));
      values.userNotificationMessages.forEach(item => {
        // eslint-disable-next-line no-param-reassign
        item.isUnread = false;
      });
    };

    const saveSettings = useCallback(() => {
      if (!settings) return;

      dispatch(adminUpdateSettings({ ...settings, value: settingValue }, settings.id));
    }, [settingValue, settings]);

    const onChangeCountDays = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = e.target;

      if (value === '') {
        setSettingValue('');

        return;
      }

      if (!/^[0-9]\d*$/.test(value)) return;

      setSettingValue(parseInt(value, 10).toString());
    }, []);

    let countDaysFieldError = '';

    if (!settingValue) {
      countDaysFieldError = 'Обязательно к заполнению';
    } else if (parseInt(settingValue, 10) < 0 || parseInt(settingValue, 10) > 180) {
      countDaysFieldError = 'Значение может быть в диапазоне от 0 до 180';
    }

    return (
      <form onSubmit={handleSubmit}>
        <div>
          {isAdmin && settings && (
            <Row justify="start">
              <FieldBlock
                label={'Количество рабочих дней, за которые надо напоминать о сроках планов'}
                color={'black'}
                placementLabel="left"
                type="not-space"
                size="content"
                className={styles.SettingsWrapper}
              >
                <TextInput
                  placeholder="Количество дней"
                  value={settingValue}
                  onChange={onChangeCountDays}
                  error={countDaysFieldError}
                />
                <Button
                  className={styles.SaveButton}
                  variant="contained"
                  id="saveButton"
                  color="primary"
                  text="Сохранить"
                  disabled={!!countDaysFieldError}
                  onClick={saveSettings}
                />
              </FieldBlock>
            </Row>
          )}
          <Row>
            <FieldBlock>
              <div style={{ display: 'flex' }}>
                <span style={{ fontWeight: 'bold', fontSize: 'xx-large' }}>Уведомления</span>
                <SelectInputField
                  className={styles.FieldReadStatus}
                  name="searchType"
                  options={notificationReadStatusOptions}
                  onChange={handleChangeSearchType}
                  defaultValue={defaultNotificationReadOption}
                  isClearable={false}
                />
                <TextInputField
                  className={styles.FieldSearchString}
                  name="searchText"
                  placeholder="Поиск"
                  onChange={e => dispatch(userNotificationSetSearchText(e.target.value))}
                />
                <Button
                  className={styles.SaveButton}
                  variant="contained"
                  id="saveButton"
                  color="primary"
                  type="submit"
                  text="Найти"
                />
                <Button
                  className={cn('shared-btn-add', styles.ButtonReadAll)}
                  type="button"
                  variant="linkButton"
                  color="primary"
                  text="Прочитать все"
                  onClick={() => handleSetReadAll()}
                  disabled={userNotificationUnreadCount === 0}
                />
              </div>
            </FieldBlock>
          </Row>

          <NotificationRows
            handleOnNotificationMessageItemClick={handleOnNotificationMessageItemClick}
            lastVisibleItemsNumber={lastVisibleItemsNumber}
          />

          {isLoadMoreButtonVisible && (
            <Button type="button" text="Показать еще" color="load" onClick={handleLoadMoreButtonClick} />
          )}
        </div>
      </form>
    );
  }

  return (
    <>
      <Form<IUserNotificationForm>
        onSubmit={handleFormSubmit}
        render={renderForm}
        initialValues={initialValues}
        mutators={{ ...arrayMutators }}
        validateOnBlur
      />
    </>
  );
};

const mapStateToProps = (state: IRootState) => ({
  userNotificationMessages: getUserNotificationMessages(state),
  selectedNotificationReadOption: getReadTypeSelectOption(state),
  userNotificationUnreadCount: getUserNotificationUnreadCount(state),
  isAdmin: getUserRoleIsAdmin(state),
  settings: getUserNotificationSettings(state),
  totalCount: getUserNotificationMessagesTotalCount(state),
  searchText: getUserNotificationSearchText(state),
  page: getUserNotificationPage(state),
});

export const UserNotificationForm = connect(mapStateToProps)(UserNotificationWithoutStore);

interface INotificationRows {
  lastVisibleItemsNumber: number;
  handleOnNotificationMessageItemClick: (
    index: number,
    data: INotificationRecord,
    setIsUnreadToFalse?: boolean,
  ) => void;
}

const NotificationRows = memo(({ lastVisibleItemsNumber, handleOnNotificationMessageItemClick }: INotificationRows) => {
  return (
    <FieldArray name="userNotificationMessages">
      {({ fields }: FieldArrayRenderProps<INotificationRecord, HTMLElement>) => {
        if (!fields.length) {
          return null;
        }

        return fields.map((name, index) => {
          const notificationRecord: INotificationRecord = fields.value?.[index];

          if (index > lastVisibleItemsNumber - 1) {
            return null;
          }

          return (
            <UserNotificationRow
              key={notificationRecord.id}
              notification={notificationRecord}
              handleClick={setIsUnreadFalse =>
                handleOnNotificationMessageItemClick(index, fields.value[index], setIsUnreadFalse)
              }
            />
          );
        });
      }}
    </FieldArray>
  );
});
